/**
 * AnchorBar — anchor-search input (bd deepcontext-d68.6).
 *
 * Ported from `vytas-deepcontext-clusters/src/components/PromptBar.jsx` to
 * TypeScript, renamed to reflect anchor-search semantics. The source repo
 * scored briefs client-side via `lib/fit.js`; here we call
 * `POST /api/clusters/score` instead (server-side scoring per d68.4).
 *
 * Anchor modes (bd deepcontext-dmpab):
 *   - A segmented toggle [ Blueprint | Free brief ] sits inline in the bar,
 *     and only the active mode's input is shown.
 *   - BLUEPRINT — a searchable typeahead over the user's Brand Blueprints.
 *     Type to filter by name, pick one to anchor via `onAnchorBlueprint(id)`
 *     (replaces the old native <select>). Default mode when ≥1 blueprint exists.
 *   - FREE BRIEF — the original free-text field: controlled `value` input,
 *     submit on Enter/click, debounced (~300ms) keystroke autosearch. Empty
 *     value uses Clear. Behaviour unchanged from before the toggle.
 *
 * The toggle + Blueprint default appear only when ≥1 blueprint exists; with
 * zero blueprints the bar degrades to the free-text field alone (pre-feature
 * behaviour). Flipping the toggle clears the active anchor (via `onClear`) and
 * resets the leaving mode's draft — toggling alone never silently re-anchors.
 *
 * Locked mockup chrome (bd deepcontext-0ihe, restyled bd deepcontext-l8re):
 * the component renders THREE top-level siblings (a Fragment, not one
 * wrapping element) so ClustersPage's controls row can lay them out per the
 * Blueprints-toolbar anatomy — a SearchInput-style pill (icon + chip/input +
 * stats, `flex-1`), the Blueprint/Free-brief toggle next to it, and the
 * gradient Anchor/Refine (+ Clear) action pinned to the far right of the
 * whole row via `order-last` (so MarketSelector/UrlCountBadge, rendered by
 * ClustersPage in between, land visually before it). Anchoring collapses the
 * pill's input away in favour of the accent chip + bold stats line; the
 * blueprint typeahead dropdown still overlays below the pill via
 * `position: absolute`.
 *
 * All chrome uses host UI primitives (Button + an inline styled <input> — no
 * Input primitive exists in `components/ui/`, so the inline <input> matches the
 * host's Tailwind palette).
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {MagnifyingGlassIcon} from '@heroicons/react/24/outline';
import {Button} from '../../components/ui/button';

const DEFAULT_DEBOUNCE_MS = 300;

type AnchorMode = 'blueprint' | 'brief';

/**
 * A Brand Blueprint the user can anchor to (deepcontext-iasew). Mirrors the
 * minimal shape of `BrandSafetyPrompt` the selector needs — id + display name.
 */
export interface AnchorBlueprintOption {
    id: string;
    name: string;
}

interface AnchorBarProps {
    /** Current anchor value (the brief that the parent has accepted). */
    value: string;
    /** Fired when the user submits a new brief or pauses typing past debounce. */
    onAnchor: (brief: string) => void;
    /** Fired when the user clears the anchor. */
    onClear: () => void;
    /** Optional stats line shown when an anchor is active. */
    segmentCount?: number;
    /**
     * Unit label for `segmentCount` (deepcontext-b3to.8 revision). The
     * story cutover repointed scoring onto `StoryFitScore[]` — which has no
     * per-story matched-URL breakdown, so `segmentCount` is now "how many
     * live stories were scored", not a URL count. Defaults to `'URLs'` for
     * callers that still score a URL-shaped segment; `ClustersPage` passes
     * `'stories'`.
     */
    segmentLabel?: string;
    avgFit?: number;
    /**
     * Human-readable label of the selected language market (e.g. "English").
     * The Universe is language-segmented: anchoring always scores against the
     * selected language market, irrespective of any blueprint's country. We
     * surface the market in the anchor result so a fit scored in (say) English
     * doesn't read as a silent bug for a blueprint from another country.
     */
    marketLabel?: string;
    /** True while a scoring request is in flight. */
    loading?: boolean;
    debounceMs?: number;
    /**
     * Brand Blueprints available to anchor to (deepcontext-iasew). When
     * non-empty, the Blueprint/Free-brief toggle renders and Blueprint is the
     * default mode. Omitted/empty → toggle hidden, free-text only (behaviour
     * identical to the pre-iasew AnchorBar).
     */
    blueprints?: AnchorBlueprintOption[];
    /**
     * True while the parent's blueprint fetch is still in flight
     * (deepcontext-vpbwl). The Blueprint/Free-brief toggle renders immediately
     * on mount when this is true — even before any blueprint has loaded — so it
     * never "pops in" once the fetch resolves. Once loading finishes, normal
     * `blueprints.length` gating applies (zero blueprints ⇒ toggle hides).
     */
    blueprintsLoading?: boolean;
    /** Currently anchored blueprint id (null when the anchor is free-text). */
    selectedBlueprintId?: string | null;
    /** Fired when the user picks a Brand Blueprint from the typeahead. */
    onAnchorBlueprint?: (blueprintId: string) => void;
}

const AnchorBar: React.FC<AnchorBarProps> = ({
    value,
    onAnchor,
    onClear,
    segmentCount,
    segmentLabel = 'URLs',
    avgFit,
    marketLabel,
    loading = false,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    blueprints = [],
    blueprintsLoading = false,
    selectedBlueprintId = null,
    onAnchorBlueprint,
}) => {
    const hasBlueprints = blueprints.length > 0;
    // deepcontext-vpbwl: the toggle is visible the instant the page mounts —
    // while blueprints are still loading OR once at least one has arrived. It
    // only hides when the fetch has FINISHED and returned zero blueprints
    // (pre-feature free-text-only behaviour).
    const showToggle = hasBlueprints || blueprintsLoading;

    // Default mode is Blueprint when ≥1 blueprint exists, else Free brief. If
    // an anchor is already active when the toggle first mounts, honour its
    // source (a blueprint anchor → Blueprint mode, a free-text anchor → Brief).
    const [mode, setMode] = useState<AnchorMode>(() => {
        // While blueprints are still loading we default to Blueprint mode so the
        // toggle mounts with Blueprint pre-selected and doesn't flip once data
        // arrives (deepcontext-vpbwl).
        if (!hasBlueprints && !blueprintsLoading) return 'brief';
        if (value.trim() && selectedBlueprintId == null) return 'brief';
        return 'blueprint';
    });

    // Blueprints can load asynchronously after first render. Adopt the
    // Blueprint default exactly once when they arrive, but only while no anchor
    // is active and the user hasn't already chosen a mode by interacting.
    const userPickedModeRef = useRef(false);
    useEffect(() => {
        if (!hasBlueprints && !blueprintsLoading) {
            // Fetch finished with zero blueprints — force free-text mode (the
            // toggle is hidden anyway).
            setMode('brief');
            return;
        }
        if (!userPickedModeRef.current && !value.trim()) {
            setMode('blueprint');
        }
    }, [hasBlueprints, blueprintsLoading, value]);

    // --- Free-brief draft + debounce -------------------------------------
    const [draft, setDraft] = useState<string>(value);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastFiredRef = useRef<string>(value);

    // --- Blueprint typeahead query + keyboard highlight ------------------
    const [query, setQuery] = useState('');
    // Index of the keyboard-highlighted suggestion (-1 = none highlighted).
    const [highlight, setHighlight] = useState(-1);
    // Refs to the suggestion <li> nodes so the active item can be scrolled
    // into view as the highlight moves (deepcontext-8gyp1).
    const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

    // Keep the local draft in sync if the parent resets the anchor.
    useEffect(() => {
        setDraft(value);
        lastFiredRef.current = value;
    }, [value]);

    // Debounced autosearch (free-brief mode only): when the user pauses, fire
    // onAnchor with the trimmed draft. An empty trimmed draft is suppressed
    // (use Clear instead). The blueprint typeahead never autosearches — it
    // anchors only on explicit selection.
    useEffect(() => {
        if (mode !== 'brief') return;
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            const trimmed = draft.trim();
            if (!trimmed) return;
            if (trimmed === lastFiredRef.current) return;
            lastFiredRef.current = trimmed;
            onAnchor(trimmed);
        }, debounceMs);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [draft, debounceMs, onAnchor, mode]);

    const anchorActive = value.trim().length > 0;
    const inBlueprintMode = mode === 'blueprint' && showToggle;

    function handleSwitchMode(next: AnchorMode) {
        if (next === mode) return;
        userPickedModeRef.current = true;
        // Switching modes clears the active anchor and resets the leaving
        // mode's draft/query. Re-anchoring happens only on submit/select in the
        // new mode — toggling alone never silently re-anchors.
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        setDraft('');
        lastFiredRef.current = '';
        setQuery('');
        setMode(next);
        if (anchorActive) onClear();
    }

    // deepcontext-l8re: the gradient Anchor/Refine button now renders OUTSIDE
    // the <form> (it's pinned to the far right of the whole controls row via
    // `order-last`), so it can no longer rely on being a native type="submit"
    // descendant. `fireSubmit` is the shared submit logic; the <form>'s
    // onSubmit (still exercised directly via `fireEvent.submit(form)` in
    // tests) and the button's onClick both call it.
    function fireSubmit() {
        const v = draft.trim();
        if (!v) return;
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        lastFiredRef.current = v;
        onAnchor(v);
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        fireSubmit();
    }

    // Mirrors the (no-op) blueprint-mode form submit: in blueprint mode,
    // anchoring only happens via explicit suggestion selection, never via the
    // Anchor button/Enter key.
    function handleActionClick() {
        if (inBlueprintMode) return;
        fireSubmit();
    }

    function handleSelectBlueprint(id: string) {
        if (!id) return;
        if (!onAnchorBlueprint) return;
        // A blueprint anchor supersedes any pending free-text autosearch.
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        setQuery('');
        onAnchorBlueprint(id);
    }

    function handleClear() {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        setDraft('');
        lastFiredRef.current = '';
        setQuery('');
        onClear();
    }

    // Filtered + alphabetised (case-insensitive) blueprint matches. The list is
    // always A→Z by name so results stay in a predictable order regardless of
    // the DB/insertion order of `blueprints` (deepcontext-8gyp1).
    const filteredBlueprints = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matches = q
            ? blueprints.filter((b) => b.name.toLowerCase().includes(q))
            : blueprints;
        return [...matches].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}),
        );
    }, [blueprints, query]);

    // Type-to-open: the suggestion dropdown stays hidden until the user types
    // ≥1 character. Clearing the input re-hides it (deepcontext-8gyp1).
    const dropdownOpen = inBlueprintMode && query.trim().length > 0;

    // Reset the highlight whenever the dropdown closes or its contents change,
    // so a stale index never points past the end of the filtered list.
    useEffect(() => {
        setHighlight(-1);
    }, [query, mode]);

    // Keep the keyboard-highlighted option scrolled into view.
    useEffect(() => {
        if (!dropdownOpen || highlight < 0) return;
        const el = optionRefs.current[highlight];
        // scrollIntoView is unavailable in some test environments (jsdom).
        el?.scrollIntoView?.({block: 'nearest'});
    }, [highlight, dropdownOpen]);

    function handleQueryKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Escape') {
            // Esc closes the dropdown by clearing the query (which re-hides it).
            if (query.length > 0) {
                e.preventDefault();
                setQuery('');
            }
            return;
        }
        if (!dropdownOpen || filteredBlueprints.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => (h + 1) % filteredBlueprints.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) =>
                h <= 0 ? filteredBlueprints.length - 1 : h - 1,
            );
        } else if (e.key === 'Enter') {
            // Enter anchors the highlighted suggestion (or the sole match when
            // nothing is explicitly highlighted yet).
            e.preventDefault();
            const idx = highlight >= 0 ? highlight : 0;
            const bp = filteredBlueprints[idx];
            if (bp) handleSelectBlueprint(bp.id);
        }
    }

    // deepcontext-0ihe: the anchor chip reads as a light accent pill (the
    // mockup's `--nav-on` bg / `--accent-ink` text), not a solid accent fill —
    // it sits inline in the pill bar next to the segmented toggle, so it needs
    // to read as "selected", not as a CTA.
    const chip = anchorActive ? (
        <span
            data-testid="anchor-chip"
            className="inline-flex flex-shrink-0 items-center gap-1.5 bg-nav-active text-accent-active text-xs font-bold rounded-full px-3 py-1"
        >
            {value.split(/[—–-]/)[0].trim().slice(0, 40)}
        </span>
    ) : null;

    // deepcontext-0ihe: the mockup bolds the counts themselves ("<b>41,208
    // URLs</b> · <b>72%</b> avg fit …") while the connective words stay
    // muted-weight — `anchor-stats-count`/`anchor-stats-fit` give the theme
    // lock test stable hooks onto those bold spans.
    const stats =
        anchorActive && segmentCount != null && avgFit != null ? (
            <span
                data-testid="anchor-stats"
                className="min-w-0 flex-shrink truncate text-xs text-ink-muted font-medium"
            >
                <b data-testid="anchor-stats-count" className="text-ink font-extrabold">
                    {segmentCount.toLocaleString()} {segmentLabel}
                </b>{' '}
                ·{' '}
                <b data-testid="anchor-stats-fit" className="text-ink font-extrabold">
                    {(avgFit * 100).toFixed(0)}%
                </b>{' '}
                avg fit
                {marketLabel && (
                    <span
                        data-testid="anchor-market-cue"
                        className="font-normal text-ink-muted"
                        title="The Universe is language-segmented. Fit is scored against the selected language market, not a campaign's country."
                    >
                        {' '}
                        · in {marketLabel}
                    </span>
                )}
            </span>
        ) : null;

    const loadingIndicator = loading ? (
        <span data-testid="anchor-loading" className="text-xs text-ink-muted">
            Scoring…
        </span>
    ) : null;

    const actions = (
        <>
            <Button type="button" size="md" variant="default" onClick={handleActionClick}>
                {anchorActive ? 'Refine' : 'Anchor'}
            </Button>
            {anchorActive && (
                <Button
                    type="button"
                    size="md"
                    variant="ghost"
                    onClick={handleClear}
                >
                    Clear
                </Button>
            )}
        </>
    );

    // deepcontext-l8re: the toggle now renders as a sibling NEXT TO the
    // search pill rather than nested inside its bordered/rounded-full chrome
    // (the pill anatomy now mirrors components/ui/SearchInput.tsx — icon,
    // chip/input, stats — matching the locked mockup). The toggle's own
    // state/handlers are unchanged; only where it renders moved.
    const modeToggle = showToggle ? (
        <div
            role="group"
            aria-label="Anchor mode"
            className="inline-flex flex-shrink-0 items-center rounded-full bg-ghost-bg p-1 h-[42px] text-xs font-bold"
        >
            <button
                type="button"
                data-testid="anchor-mode-blueprint"
                aria-pressed={mode === 'blueprint'}
                onClick={() => handleSwitchMode('blueprint')}
                className={`flex h-full items-center rounded-full px-3 transition-colors ${
                    mode === 'blueprint'
                        ? 'bg-surface text-ink shadow-sm'
                        : 'text-ink-muted hover:text-ink'
                }`}
            >
                Blueprint
            </button>
            <button
                type="button"
                data-testid="anchor-mode-brief"
                aria-pressed={mode === 'brief'}
                onClick={() => handleSwitchMode('brief')}
                className={`flex h-full items-center rounded-full px-3 transition-colors ${
                    mode === 'brief'
                        ? 'bg-surface text-ink shadow-sm'
                        : 'text-ink-muted hover:text-ink'
                }`}
            >
                Free brief
            </button>
        </div>
    ) : null;

    const modeInput = inBlueprintMode ? (
        <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleQueryKeyDown}
            placeholder="Search brand blueprints…"
            aria-label="Search brand blueprints"
            role="combobox"
            aria-expanded={dropdownOpen}
            aria-controls="blueprint-options"
            aria-autocomplete="list"
            className="flex-1 min-w-[160px] bg-transparent outline-none text-sm text-ink placeholder:text-ink-muted"
        />
    ) : (
        <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
                anchorActive
                    ? 'Refine the brand or brief…'
                    : 'Type a brand or brief: e.g. Nike — performance running for young men'
            }
            aria-label="Brand or brief"
            className={`flex-1 bg-transparent outline-none text-sm text-ink placeholder:text-ink-muted ${
                anchorActive ? 'min-w-0' : 'min-w-[160px]'
            }`}
        />
    );

    // deepcontext-l8re: `AnchorBar` now renders THREE top-level siblings
    // (a Fragment, not one wrapping element) so the parent's flex row can
    // place them per the locked mockup: pill (flex-1) → toggle → ...(page-
    // level MarketSelector/UrlCountBadge)... → gradient Anchor action pinned
    // to the far right via `order-last`. The pill keeps the chip + stats
    // inside it when anchored, and the blueprint typeahead dropdown still
    // overlays directly under the pill — behaviourally unchanged from before
    // the restyle, only the surrounding chrome/layout moved.
    return (
        <>
            {/* bd deepcontext-r4xu: `flex-1 min-w-0` (rather than relying on
                block-level default sizing) makes the pill grow to fill the
                row, matching the locked mockup's search bar width. */}
            <div className="relative flex-1 min-w-0" data-testid="anchor-bar">
                <form
                    onSubmit={inBlueprintMode ? (e) => e.preventDefault() : handleSubmit}
                    className="flex w-full items-center gap-2.5 bg-surface border border-line rounded-full pl-4 pr-2 h-[42px] text-sm shadow-sm"
                    aria-label="Anchor search"
                >
                    <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-ink-muted" />
                    {chip}
                    {modeInput}
                    {stats}
                    {loadingIndicator}
                </form>

                {/* Typeahead match list. Type-to-open: shown only while the query
                    is non-empty; clearing the input re-hides it (deepcontext-8gyp1). */}
                {inBlueprintMode && dropdownOpen && (
                    <ul
                        id="blueprint-options"
                        role="listbox"
                        data-testid="blueprint-options"
                        className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-md border border-line bg-surface shadow-lg"
                    >
                        {filteredBlueprints.length === 0 ? (
                            <li className="px-3 py-2 text-xs text-ink-muted">
                                No brand blueprints found
                            </li>
                        ) : (
                            filteredBlueprints.map((bp, i) => {
                                const highlighted = i === highlight;
                                return (
                                    <li
                                        key={bp.id}
                                        role="option"
                                        aria-selected={highlighted}
                                        ref={(el) => {
                                            optionRefs.current[i] = el;
                                        }}
                                    >
                                        <button
                                            type="button"
                                            data-testid="blueprint-option"
                                            data-highlighted={
                                                highlighted ? 'true' : undefined
                                            }
                                            aria-pressed={bp.id === selectedBlueprintId}
                                            onClick={() => handleSelectBlueprint(bp.id)}
                                            onMouseEnter={() => setHighlight(i)}
                                            className={`block w-full px-3 py-2 text-left text-sm hover:bg-ghost-bg ${
                                                highlighted ? 'bg-canvas' : ''
                                            } ${
                                                bp.id === selectedBlueprintId
                                                    ? 'font-medium text-ink'
                                                    : 'text-ink'
                                            }`}
                                        >
                                            <span className="block truncate">
                                                {bp.name}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                )}
            </div>

            {modeToggle}

            {/* deepcontext-l8re: the gradient Anchor/Refine (+ Clear) action
                is pinned to the FAR RIGHT of the whole controls row via
                `order-last` — visually after MarketSelector/UrlCountBadge,
                which are page-level siblings rendered after this component
                by ClustersPage, even though this element renders earlier in
                DOM order (it's owned by AnchorBar's own state). */}
            <div
                className="order-last flex flex-shrink-0 items-center gap-1.5"
                data-testid="anchor-actions"
            >
                {actions}
            </div>
        </>
    );
};

export default AnchorBar;
